import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { CalendarClock, LayoutDashboard, Package, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import type { TabItem } from "@/components/TabNavigation";
import { TabNavigation } from "@/components/TabNavigation";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/equipment")({
	// The layout is just the tab shell (no data) and must render offline so the
	// ready children (fleet list, breakdown report) stay reachable. Equipment is
	// a mixed-stance section: every child route declares its own stance — do NOT
	// rely on inheriting this "ready".
	staticData: { offline: "ready" },
	component: EquipmentLayout,
});

function EquipmentLayout() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const location = useLocation();

	const basePath = `/app/org/${orgId}/equipment`;
	const params = { orgId };

	const tabs: TabItem[] = [
		{
			key: "dashboard",
			label: t("equipment.tabs.dashboard"),
			to: "/app/org/$orgId/equipment",
			params,
			icon: <LayoutDashboard className="h-4 w-4" />,
		},
		{
			key: "fleet",
			label: t("equipment.tabs.fleet"),
			to: "/app/org/$orgId/equipment/fleet",
			params,
			icon: <Truck className="h-4 w-4" />,
		},
		{
			key: "parts",
			label: t("equipment.tabs.parts"),
			to: "/app/org/$orgId/equipment/parts",
			params,
			icon: <Package className="h-4 w-4" />,
		},
		{
			key: "schedule",
			label: t("equipment.tabs.schedule"),
			to: "/app/org/$orgId/equipment/schedule",
			params,
			icon: <CalendarClock className="h-4 w-4" />,
		},
	];

	const getActiveTab = (): string => {
		const pathname = location.pathname;
		if (pathname.includes(`${basePath}/fleet`)) return "fleet";
		if (pathname.includes(`${basePath}/parts`)) return "parts";
		if (pathname.includes(`${basePath}/schedule`)) return "schedule";
		if (pathname.includes(`${basePath}/breakdown-report`)) return "dashboard";
		return "dashboard";
	};

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("equipment.nav.title")} />
			<TabNavigation tabs={tabs} activeTab={getActiveTab()} />
			<Outlet />
		</div>
	);
}
